import { Injectable, OnDestroy } from '@angular/core';
import { MatSnackBar, MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { fromEvent, merge, Subscription } from 'rxjs';
import { first, throttleTime } from 'rxjs/operators';

const LAST_USER_ACTIVITY_STORAGE_KEY = 'auth-last-user-activity';
const INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000;
const INACTIVITY_WARNING_MS = 5 * 60 * 1000;
const ACTIVITY_THROTTLE_MS = 30000;

@Injectable({ providedIn: 'root' })
export class SessionInactivityService implements OnDestroy {
    private isSessionActive: () => boolean = () => false;
    private onTimeout: () => void = () => undefined;
    private readonly subscriptions = new Subscription();
    private warning: MatSnackBarRef<SimpleSnackBar>;
    private started = false;

    constructor(private readonly snackBar: MatSnackBar) { }

    public start(isSessionActive: () => boolean, onTimeout: () => void): void {
        this.isSessionActive = isSessionActive;
        this.onTimeout = onTimeout;

        if (this.started) {
            return;
        }

        this.started = true;
        if (this.isSessionActive() && !localStorage.getItem(LAST_USER_ACTIVITY_STORAGE_KEY)) {
            this.reset();
        }

        this.subscriptions.add(merge(
            fromEvent(document, 'mousedown'),
            fromEvent(document, 'mousemove'),
            fromEvent(document, 'keydown'),
            fromEvent(document, 'touchstart'),
            fromEvent(document, 'wheel')
        ).pipe(throttleTime(ACTIVITY_THROTTLE_MS)).subscribe(() => this.recordActivity()));

        this.subscriptions.add(fromEvent<StorageEvent>(window, 'storage').subscribe(event => {
            if (event.key === LAST_USER_ACTIVITY_STORAGE_KEY && event.newValue) {
                this.dismissWarning();
            }
        }));
    }

    public check(): boolean {
        if (!this.isSessionActive()) {
            return false;
        }

        const lastActivity = Number(localStorage.getItem(LAST_USER_ACTIVITY_STORAGE_KEY));
        if (!Number.isFinite(lastActivity) || lastActivity <= 0) {
            this.reset();
            return false;
        }

        const inactiveFor = Date.now() - lastActivity;
        if (inactiveFor >= INACTIVITY_TIMEOUT_MS) {
            this.onTimeout();
            return true;
        }

        if (inactiveFor >= INACTIVITY_TIMEOUT_MS - INACTIVITY_WARNING_MS) {
            this.showWarning();
        } else {
            this.dismissWarning();
        }

        return false;
    }

    public reset(): void {
        localStorage.setItem(LAST_USER_ACTIVITY_STORAGE_KEY, Date.now().toString());
        this.dismissWarning();
    }

    public dismissWarning(): void {
        if (!this.warning) {
            return;
        }

        const warning = this.warning;
        this.warning = null;
        warning.dismiss();
    }

    public clear(): void {
        localStorage.removeItem(LAST_USER_ACTIVITY_STORAGE_KEY);
        this.dismissWarning();
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.dismissWarning();
    }

    private recordActivity(): void {
        if (!this.isSessionActive() || this.check()) {
            return;
        }

        this.reset();
    }

    private showWarning(): void {
        if (this.warning) {
            return;
        }

        const warning = this.snackBar.open(
            'You will be signed out in 5 minutes due to inactivity.',
            'Stay signed in',
            { duration: INACTIVITY_WARNING_MS, horizontalPosition: 'center', verticalPosition: 'top' }
        );

        this.warning = warning;
        warning.onAction().pipe(first()).subscribe(() => this.reset());
        warning.afterDismissed().pipe(first()).subscribe(() => {
            if (this.warning === warning) {
                this.warning = null;
            }
        });
    }
}
